/**
 * A checklist row — a stage-gate criterion, done or not.
 *
 * The state is carried by a filled-or-empty disc *and* by an accessible name,
 * never by the disc alone (PRD §9.7). The tick glyph inside the done state is
 * decorative: it repeats what the label already says, so it is hidden from
 * assistive technology rather than announced as "check".
 *
 * Not a checkbox. These rows are derived — a criterion is met because the data
 * says so — and rendering them as inputs would invite a user to click one and
 * wonder why nothing happened.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 6.5.8, PRD §9.7
 */
import { useTranslation } from 'react-i18next';

import { cn } from '../../lib/cn';

export interface GateProps {
  done: boolean;
  children: React.ReactNode;
  className?: string;
}

export function Gate({ done, children, className }: GateProps): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <div
      className={cn(
        'border-gray-100 flex items-start gap-2.5 border-b py-2.5 text-[13px] last:border-b-0',
        className,
      )}
    >
      <span
        className={cn(
          'mt-0.5 grid size-4 flex-none place-items-center rounded-full text-[9px] font-extrabold',
          done ? 'bg-green-brand text-navy' : 'border-gray-200 border-[1.5px] bg-white',
        )}
        aria-hidden="true"
      >
        {done ? '✓' : ''}
      </span>
      <span className="text-body min-w-0 flex-1">
        {/* The state in words, for anyone who cannot see the disc. Visually
            hidden because sighted users already have it. */}
        <span className="sr-only">{done ? t('gate.met') : t('gate.unmet')}: </span>
        {children}
      </span>
    </div>
  );
}
